import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { startWith, takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';

import { UserModel } from '@data-layer/models/user';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';
import { ACCESS_TOKEN } from '@constants/setting-keys.const';
import { JwtHelperService } from '@auth0/angular-jwt';

@Component({
  selector: 'company-info',
  templateUrl: './company-info.component.html',
  styleUrls: ['./company-info.component.scss'],
})
export class CompanyInfoComponent implements OnInit, OnDestroy {
  @Input() public open: boolean = false;
  @Output() public openChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  public user: UserModel;
  public company: CompanyModel;
  public companies: CompanyModel[] = [];

  private isDestroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(private companiesService: CompaniesService, private jwtHelperService: JwtHelperService) {}

  public ngOnInit(): void {
    this.user = this.companiesService.getUser();
    this.companiesService.companyChange$.pipe(takeUntil(this.isDestroy)).subscribe((changeCompany) => {
      this.company = changeCompany.current;
    });

    this.companiesService.companiesChange$
      .pipe(takeUntil(this.isDestroy), startWith({ current: this.companiesService.getCompanies() }))
      .subscribe((changeCompany) => {
        this.companies = changeCompany.current;
      });
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public get fullName(): string {
    return this.company ? this.company.businessName : `${this.user.firstName} ${this.user.lastName}`;
  }

  public get smallName(): string {
    return `${this.user.firstName.charAt(0)}${this.user.lastName.charAt(0)}`.toUpperCase();
  }

  public get logoUri(): string {
    return this.company ? this.company.logoUri : null;
  }

  public toggleOpen(): void {
    this.openChange.emit(!this.open);
  }

  public selectCompany(event: Event, company: CompanyModel, callback?: () => void) {
    event.preventDefault();
    event.stopPropagation();
    this.loadUserBetaFlags(company);
    this.companiesService
      .getCompanyAsync(true, true, company?.id)
      .pipe(takeUntil(this.isDestroy))
      .subscribe(() => {
        this.companiesService.setDefaultCompany(company, callback);
      });
  }

  private loadUserBetaFlags(company?: CompanyModel): void {
    this.companiesService
      .getUserBetaFlags({
        userExternalId: this.userExternalId,
        companyId: !!company ? company.id : this.company.id,
        organizationId: !!company ? company.organizationId : this.company.organizationId,
      })
      .pipe(takeUntil(this.isDestroy))
      .subscribe((betaFlags) => {
        if (!!betaFlags) {
          this.companiesService.setUserBetaFlags(betaFlags);
        }
      });
  }

  private get userExternalId(): string {
    return this.jwtHelperService.decodeToken(localStorage.getItem(ACCESS_TOKEN))?.user_external_id || null;
  }
}
